<template>
  <BasePage :page-title="__DICT__.actionMenu.liftPass" back-button show-menu>
    <!-- Loading State (including PWA background recovery) -->
    <LoadingSpinner v-if="loading || isRecovering" />

    <!-- Main Content -->
    <div v-else-if="liftPasses && isReady" class="d-flex flex-column" style="min-height: calc(100vh - 300px)">
      <!-- Progress Bar -->
      <div class="mb-3">
        <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
        {{collectionLoc}} - {{collectionAllocTime}} <br/>
        <ProgressBar
            :percent="(liftPasses.distributed/liftPasses.total)*100"
            :total="liftPasses.total"
            :raw="liftPasses.distributed"
            :heading="__DICT__.distributed"
        />

        <QrScanner
            class="mt-2"
            v-if="isScanning"
            :allow-partial="false"
            v-model="showingScanner"
            @code-scanned="(code, scan) => handlePassLookup('scan', code, scan)"
            @invalid-code="handleInvalidCode"
            @close="handleClose"
        />
        <!-- Pass Information Display -->
        <template v-if="currentPass">
          <Card title="Lift Pass Information" show-status-bar margin="">
            <!-- Pass Information -->
            <table class="table table-vcenter" v-if="currentPass.deleted === '0' && !currentPass.distributed">
              <tbody>
              <tr>
                <td>{{ __DICT__.name }}</td>
                <td>{{ currentPass.name }}</td>
              </tr>
              <tr>
                <td>{{ __DICT__.booking }}</td>
                <td>{{ currentPass.bookingRef }}</td>
              </tr>
              <tr>
                <td>{{ __DICT__.distributed }}</td>
                <td>{{ currentPass.distributed ? __DICT__.yes : __DICT__.no }}</td>
              </tr>
              <tr>
                <td>{{ __DICT__.passType }}</td>
                <td>{{ currentPass.passType }}</td>
              </tr>
              </tbody>
            </table>
            <div v-else-if="!currentPass.distributed" class="alert alert-danger">
              {{ __DICT__.liftPassDist.passAlreadyDistributedBefore }} {{currentPass.name}} ({{currentPass.bookingRef}}) {{ __DICT__.liftPassDist.passAlreadyDistributedAfter }}
            </div>
            <div v-else class="alert alert-danger">
              {{ __DICT__.liftPassDist.passNotDistributable }}
            </div>
          </Card>
          <div class="mt-2 alert alert-info">
            {{ __DICT__.liftPassDist.ensurePhysicalMatch }}
          </div>
        </template>

        <div class="accordion mt-4 mb-6" id="paxAccordion">
          <AccordionItem id="collapseDistributed" parent-id="paxAccordion" expanded>
            <template #header>
              <span>{{ __DICT__.distributed }}</span>
              <Badge v-if="distributedPax.length" :text="distributedPax.length" class="ms-2" />
            </template>
            <PaxAllocation title="Distributed" :inhabitants="distributedPax"></PaxAllocation>
          </AccordionItem>
          <AccordionItem id="collapseNotDistributed" parent-id="paxAccordion">
            <template #header>
              <span>{{ __DICT__.notDistributed }}</span>
              <Badge v-if="notDistributedPax.length" :text="notDistributedPax.length" class="ms-2" />
            </template>
            <PaxAllocation title="Not Distributed" :inhabitants="notDistributedPax"></PaxAllocation>
          </AccordionItem>
        </div>
      </div>






      <!-- Name Search Modal -->
      <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
      <NameSearchModal
          :is-open="showingNameSearch"
          :tickets="liftPasses?.tickets || []"
          :heading="`Find ${__DICT__.passenger}`"
          @close="closeNameSearch"
          @select="handlePassLookup('manual', $event)"
          show-lift-pass-satus
          liftPassStatusText="Distributed?"
      />

      <!-- Action Buttons -->
      <div class="fixed-bottom fixed-bottom-bar p-3">
        <!-- Initial State Buttons -->
        <template v-if="currentState === 'initial' && entryMethod !== 'scan'">
          <button class="btn btn-primary w-100 mb-3" @click="startScanner">
            {{ __DICT__.scanBarcode }}
          </button>
          <button class="btn btn-secondary w-100" @click="startNameSearch">
            {{ __DICT__.enterByName }}
          </button>
        </template>

        <!-- Pass Selected State Buttons -->
        <template v-else>
          <SlideConfirmButton
              v-if="currentState === 'ready'"
              title="Slide to distribute pass"
              @complete="handleDistributionSubmit"
          />
          <button
              v-if="currentPass || entryMethod === 'scan'"
              class="btn btn-secondary w-100 mt-2"
              @click="resetState"
          >
            {{ __DICT__.cancel }}
          </button>
        </template>
      </div>
    </div>

    <!-- Success Modal -->
    <div class="modal modal-blur fade" :class="{ 'show': showSuccessModal }" tabindex="-1" style="display: block;" v-if="showSuccessModal">
      <div class="modal-dialog modal-sm modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-body">
            <div class="modal-title text-success">{{ __DICT__.success }}</div>
            <div>{{ __DICT__.liftPassDist.passDistributed }} {{ currentPass?.name }}</div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-success w-100" @click="acknowledgeSuccess">
              {{ __DICT__.ok }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Error Modal -->
    <div class="modal modal-blur fade" :class="{ 'show': showErrorModal }" tabindex="-1" style="display: block;" v-if="showErrorModal">
      <div class="modal-dialog modal-sm modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-body">
            <div class="modal-title text-danger">{{ __DICT__.error }}</div>
            <div>{{ error?.message || 'An error occurred' }}</div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-danger w-100" @click="acknowledgeError">
              {{ __DICT__.ok }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Backdrop -->
    <div class="modal-backdrop fade" :class="{ 'show': showSuccessModal || showErrorModal }" v-if="showSuccessModal || showErrorModal"></div>
  </BasePage>
</template>

<script setup>
import {ref, computed, onMounted, onUnmounted, watch} from "vue";
import {rememberTicketBookings} from '@/services/qrKeyCache.js';
import { useWeekStore } from '@/stores/week.js'
import { usePermissions } from '@/composables/usePermissions'
import { api } from "@/services/api.js";
import { decodeBase64UrlToObject } from "@/services/base64.js";
import BasePage from "@/components/BasePage.vue";
import ProgressBar from "@/components/ProgressBar.vue";
import QrScanner from "@/components/QrScanner.vue";
import {qrRequestCode, qrRequestFields} from "@/utils/qrVerify.js";
import NameSearchModal from "@/components/NameSearchModal.vue";
import SlideConfirmButton from "@/components/SlideConfirmButton.vue";
import PaxAllocation from "@/components/PaxAllocation.vue";
import { AccordionItem, Badge, Card, LoadingSpinner } from "@/components/atomic";
import {useRoute, useRouter} from "vue-router";
import {useEnsureWeekData} from "@/composables/useEnsureWeekData.js";
import {useHaptics} from "@/composables/useHaptics.js";
import {useScanRedeem} from "@/composables/useScanRedeem.js";

const route = useRoute()
const { errorFeedback } = useHaptics();
const router = useRouter()
// State Management
const weekStore = useWeekStore()
const { can } = usePermissions()

// Ensure week data is available (handles PWA background recovery)
const { isRecovering, isReady } = useEnsureWeekData()

const loading = ref(true)
const liftPasses = ref(null)
// The QR check can bind a scan to its ticket's booking (qr-binding-ticket-booking).
watch(() => liftPasses.value?.tickets, rememberTicketBookings)
const currentPass = ref(null)
const entryMethod = ref(null) // 'scan' | 'manual' | null
const {processing, error, showSuccessModal, showErrorModal, redeem} = useScanRedeem({
  refresh: () => refreshData()
})
const collectionLoc = ref(null)
const collectionAllocTime = ref(null)
//const distributedPax = ref([])
//const notDistributedPax = ref([])

const distributedPax = computed(() => {
  if(liftPasses.value?.tickets?.length > 0) {
    return liftPasses.value.tickets.filter(t => t.distributed)
  } else {
    return []
  }
})

const notDistributedPax = computed(() => {
  if(liftPasses.value?.tickets?.length > 0) {
    return liftPasses.value.tickets.filter(t => !t.distributed)
  } else {
    return []
  }
})

// Computed States
const currentState = computed(() => {
  if (!currentPass.value) return 'initial'
  if (processing.value) return 'processing'
  if (currentPass.value.distributed) return 'distributed'
  if (currentPass.value.deleted === '1') return 'deleted'
  return 'ready'
})

const isScanning = computed(() =>
    currentState.value === 'initial' && entryMethod.value === 'scan'
)

const showingNameSearch = computed(() =>
    currentState.value === 'initial' && entryMethod.value === 'manual'
)

// Ref for v-model binding on QrScanner
const showingScanner = ref(false)

// Methods
const generateGuid = () => {
  const s4 = () => Math.floor((1 + Math.random()) * 0x10000)
      .toString(16)
      .substring(1);
  return `${s4()}${s4()}-${s4()}-${s4()}-${s4()}-${s4()}${s4()}${s4()}`;
}

// The scan behind currentPass: { raw, token, verification }; null for name search.
// The lookup GET keeps the bare reference in its path; the distribution POST
// carries the full string and the verification.
let currentScan = null

const handlePassLookup = async (method, identifier, scan = null) => {
  if (processing.value) return;
  processing.value = true;
  currentScan = method === 'scan' ? scan : null;
  error.value = null;

  // Check if user can distribute this pass based on brand
  if (!can('canDistributeLiftPass', { ref: identifier })) {
    error.value = {message: `You may only distribute ${__DICT__.brand.toUpperCase()} passes`};
    processing.value = false;
    showErrorModal.value = true;
    return;
  }

  // Check if the barcode exists in allocated passes
  const isAllocated = liftPasses.value.tickets.some(
      ticket => ticket.barcode === identifier
  );


  if (!isAllocated) {
    error.value = new Error("You are not allocated to this pass");
    showErrorModal.value = true;
    resetState();
    processing.value = false;
    return;
  }

  try {
    const isScanned = method === 'scan' ? 1 : 0;
    currentPass.value = await api.get(
        `/api/scanLiftPass/barcode/${identifier}/week/${weekStore.weekId}/${isScanned}`
    );
    entryMethod.value = method;
  } catch (e) {
    error.value = e;
    showErrorModal.value = true;
    resetState();
  } finally {
    processing.value = false;
  }
}

const handleDistributionSubmit = async () => {
  if (processing.value) return;

  processing.value = true;
  error.value = null;

  // Guard against null repObj (can happen when PWA resumes from background)
  if (!weekStore.repObj) {
    error.value = { message: 'Session data not available. Please go back and try again.' };
    showErrorModal.value = true;
    processing.value = false;
    return;
  }

  try {
    const payload = {
      weekID: weekStore.weekId,
      bookingRef: currentPass.value.bookingRef,
      barcode: qrRequestCode(currentScan, currentPass.value.barcode),
      ...qrRequestFields(currentScan, currentPass.value.barcode),
      handsetTimestamp: new Date().toISOString(),
      idempotentId: generateGuid(),
      repID: weekStore.repObj.repId,
      repName: weekStore.repObj.name,
      passengerName: currentPass.value.name,
      scanned: entryMethod.value === 'scan' ? 1 : 0
    };

    await redeem('/api/v3/scan/lift-pass', payload);
  } catch (e) {
    error.value = e;
    showErrorModal.value = true;
    errorFeedback();
  } finally {
    processing.value = false;
  }
}

const acknowledgeSuccess = () => {
  showSuccessModal.value = false;
  resetState();
}

const acknowledgeError = () => {
  showErrorModal.value = false;
  error.value = null;
}

const startScanner = () => {
  entryMethod.value = 'scan';
}

const startNameSearch = () => {
  entryMethod.value = 'manual';
}

const closeNameSearch = () => {
  if (!currentPass.value) {
    resetState();
  }
}

const resetState = () => {
  currentPass.value = null;
  entryMethod.value = null;
  processing.value = false;
}

const refreshData = async () => {
  const response = await api.get(`/api/scanLiftPass/allBarcodes/${weekStore.weekId}`);
  const repId = weekStore.repObj?.repId;

  // Validate locationId parameter exists
  if (!route.params.locationId) {
    console.error('[LiftPass] Missing locationId parameter, redirecting to locations');
    router.replace({ name: 'liftPass' });
    return;
  }

  // Safely decode the location parameter
  let collectionObj;
  try {
    collectionObj = decodeBase64UrlToObject(route.params.locationId);
  } catch (decodeError) {
    console.error('[LiftPass] Failed to decode locationId:', decodeError);
    // Redirect to lift pass locations page on decode error (likely old bookmark or corrupted URL)
    router.replace({ name: 'liftPass' });
    return;
  }

  const collectionLocation = collectionObj.location

  const collectionTime = collectionObj.time
  collectionLoc.value = collectionLocation
  collectionAllocTime.value = collectionTime
  const doNotFilterByRep = can('viewAllLiftPasses')

  // No filtering needed - return raw response
  if (doNotFilterByRep && collectionLocation === 'All') {
    liftPasses.value = response;
    return;
  }

  // Transform the response data
  const processedData = {
    total: 0,
    distributed: 0,
    tickets: []
  };

  if (response.tickets && Array.isArray(response.tickets)) {
    processedData.tickets = response.tickets.filter(ticket => {

      if(collectionLocation === 'All') {
        return true;
      }

      if (ticket.distributionPoint !== collectionLocation || ticket.allocationDistributionTime !== collectionTime) {
        return false;
      }

      // Filter by rep if required
      if (!doNotFilterByRep) {
        try {
          const staffMembers = JSON.parse(ticket.staffMembers);
          return staffMembers.some(staff => staff.id.toString() === repId.toString());
        } catch (e) {
          console.error('Error parsing staffMembers:', e);
          return false;
        }
      }

      return true;
    });

    processedData.total = processedData.tickets.length;
    processedData.distributed = processedData.tickets.reduce((acc, ticket) => {
      return acc + (ticket.distributed === 1 ? 1 : 0);
    }, 0);
  }
  liftPasses.value = processedData;
};
let refreshTimeout = null
const refreshDataHook = () => {
  refreshData();
  refreshTimeout = setTimeout(refreshDataHook, 10000)
}


const handleClose = () => {
  showingScanner.value = false;
}

const handleInvalidCode = () => {
  error.value = {message: 'Invalid barcode format'};
  showErrorModal.value = true;
  errorFeedback();
}

// Initial Data Load
onMounted(async () => {
  try {
    await refreshData();
    refreshDataHook()
  } finally {
    loading.value = false;
  }
})

onUnmounted(() => {
  if (refreshTimeout) {
    clearTimeout(refreshTimeout);
  }
});

</script>
